import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { BarChart3, Bell, Home, LogIn, LogOut, Menu, ShieldCheck, Sparkles, Swords, Trophy, UserRound } from 'lucide-react'
import { useEffect, useState, type MouseEvent as ReactMouseEvent } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { Brand } from './Brand'
import { Modal, UserAvatar } from './ui'

const navItems = [
  { to: '/', label: 'Início', icon: Home, end: true },
  { to: '/palpites', label: 'Palpites', icon: Swords },
  { to: '/ranking', label: 'Ranking', icon: Trophy },
  { to: '/ligas', label: 'Ligas', icon: BarChart3 },
  { to: '/perfil', label: 'Perfil', icon: UserRound }
]

export function AppShell() {
  const { user, isAdmin, isDemo, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [authPromptOpen, setAuthPromptOpen] = useState(false)
  const [returnTo, setReturnTo] = useState('/palpites')

  useEffect(() => {
    const state = location.state as { authPrompt?: boolean; returnTo?: string } | null
    if (!user && state?.authPrompt) {
      setReturnTo(state.returnTo ?? '/palpites')
      setAuthPromptOpen(true)
    }
  }, [location.state, user])

  const requiresAccount = (href: string) => {
    const pathname = new URL(href, window.location.href).pathname
    return ['/palpites', '/ranking', '/ligas', '/perfil', '/admin', '/u/'].some((path) => pathname === path || pathname.startsWith(`${path}/`) || (path.endsWith('/') && pathname.startsWith(path)))
  }

  const handleGuestInteraction = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (user) return
    const target = (event.target as HTMLElement).closest<HTMLElement>('a[href], button[data-auth-required]')
    if (!target) return
    const href = target instanceof HTMLAnchorElement ? target.href : target.dataset.authRequired
    if (!href || !requiresAccount(href)) return
    event.preventDefault()
    event.stopPropagation()
    setReturnTo(new URL(href, window.location.href).pathname)
    setAuthPromptOpen(true)
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/entrar')
  }

  return (
    <div className="app-shell" onClickCapture={handleGuestInteraction}>
      <header className="topbar">
        <div className="topbar__inner">
          <Brand />
          <nav className="desktop-nav" aria-label="Navegação principal">
            {navItems.slice(0, 4).map(({ to, label, icon: Icon, end }) => (
              <NavLink key={to} to={to} end={end} className={({ isActive }) => `desktop-nav__item ${isActive ? 'is-active' : ''}`}>
                <Icon size={17} />
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="topbar__actions">
            {isDemo && user ? <span className="demo-badge">Demonstração</span> : null}
            {!user ? <span className="guest-badge">100% grátis</span> : null}
            <button className="icon-button topbar__bell" aria-label="Notificações" data-auth-required="/perfil">
              <Bell size={20} />
              <span className="notification-dot" />
            </button>
            {user ? <DropdownMenu.Root>
              <DropdownMenu.Trigger className="profile-trigger" aria-label="Abrir menu do perfil">
                <UserAvatar name={user?.user_metadata?.display_name ?? 'Visitante'} src={user?.user_metadata?.avatar_url} size="sm" />
                <Menu size={18} />
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content className="dropdown-content" align="end" sideOffset={8}>
                  <DropdownMenu.Label className="dropdown-label">
                    <strong>{user?.user_metadata?.display_name ?? 'Visitante'}</strong>
                    <span>{user?.email ?? 'Modo demonstração'}</span>
                  </DropdownMenu.Label>
                  <DropdownMenu.Separator className="dropdown-separator" />
                  <DropdownMenu.Item className="dropdown-item" onSelect={() => navigate('/perfil')}>
                    <UserRound size={17} /> Meu perfil
                  </DropdownMenu.Item>
                  {isAdmin ? (
                    <DropdownMenu.Item className="dropdown-item" onSelect={() => navigate('/admin')}>
                      <ShieldCheck size={17} /> Administração
                    </DropdownMenu.Item>
                  ) : null}
                  <DropdownMenu.Separator className="dropdown-separator" />
                  <DropdownMenu.Item className="dropdown-item dropdown-item--danger" onSelect={handleSignOut}>
                    <LogOut size={17} /> Sair
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root> : <Link className="topbar-signup" to="/entrar" state={{ mode: 'signup', returnTo }}><Sparkles size={17} /> Criar conta</Link>}
          </div>
        </div>
      </header>

      <main className="app-content">
        <Outlet />
      </main>

      <nav className="mobile-nav" aria-label="Navegação principal">
        {navItems.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} className={({ isActive }) => `mobile-nav__item ${isActive ? 'is-active' : ''}`}>
            <Icon size={22} strokeWidth={2} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      <Modal
        open={authPromptOpen}
        onOpenChange={setAuthPromptOpen}
        title="Entre para competir"
        description="Crie sua conta grátis para salvar palpites, entrar no ranking e desafiar seus amigos."
      >
        <div className="auth-prompt">
          <img src="/brand/logo-256.webp" alt="LIGA UVM" width="86" height="86" />
          <div className="auth-prompt__copy"><strong>Seu card. Seu ranking. Sua liga.</strong><span>Leva menos de um minuto para começar.</span></div>
          <Link className="button button--primary button--full" to="/entrar" state={{ mode: 'signup', returnTo }}><Sparkles size={18} /> Criar conta grátis</Link>
          <Link className="button button--secondary button--full" to="/entrar" state={{ mode: 'login', returnTo }}><LogIn size={18} /> Já tenho uma conta</Link>
          <button type="button" className="auth-prompt__dismiss" onClick={() => setAuthPromptOpen(false)}>Continuar explorando</button>
        </div>
      </Modal>
    </div>
  )
}
